/**
 * Feature Metadata — i18n-safe marketing copy for each feature key.
 * Lives in code (NOT in the admin matrix) to keep translations safe.
 *
 * Used by:
 *   - PricingComparisonTable / PlanCard (rendered from matrix + this metadata)
 *   - RenewSubscriptionGate (label/description on locked overlays)
 *   - StudentFeatureBlocked (request-from-instructor copy)
 *   - Admin matrix screen (display only — admin cannot edit text)
 */
import { FEATURE_KEYS as K, FEATURE_CATEGORIES as C } from "./feature-keys";

/**
 * Shape:
 *   [key]: {
 *     category,
 *     label_ar, label_en,
 *     short_ar, short_en,        // pricing-card line
 *     description_ar, description_en,  // lock-overlay body
 *     marketing_ar, marketing_en,      // optional value-prop ("why upgrade")
 *     icon,                       // lucide icon name (string)
 *     student_requestable,        // true → student "request from instructor"
 *     highlight,                  // true → ✦ in pricing card
 *   }
 */
export const FEATURE_METADATA = {
  // ── core ──────────────────────────────────────────────────────────────────
  [K.ACADEMY_SITE]: { category: C.CORE, label_ar: "رابط أكاديمية مخصّص على المنصة", label_en: "Custom academy subdomain" },
  [K.UNLIMITED_COURSES]: { category: C.CORE, label_ar: "دورات غير محدودة", label_en: "Unlimited courses" },
  [K.UNLIMITED_STUDENTS]: { category: C.CORE, label_ar: "طلاب بلا حدود", label_en: "Unlimited students" },
  [K.VIDEO_LESSONS]: { category: C.CORE, label_ar: "دروس فيديو", label_en: "Video lessons" },
  [K.FILE_LESSONS]: { category: C.CORE, label_ar: "دروس ملفات", label_en: "File lessons" },
  [K.TEXT_LESSONS]: { category: C.CORE, label_ar: "دروس نصية", label_en: "Text lessons" },
  [K.PROGRESS_TRACKING]: { category: C.CORE, label_ar: "متابعة تقدّم الطلاب", label_en: "Student progress tracking" },
  [K.DRIP_CONTENT]: { category: C.CORE, label_ar: "المحتوى المتدرّج (Drip)", label_en: "Drip content scheduling" },
  [K.BRANDING]: { category: C.CORE, label_ar: "تخصيص الهوية البصرية", label_en: "Custom branding (logo, colors, fonts)" },
  [K.PAGE_BUILDER_ACADEMY]: { category: C.CORE, label_ar: "محرر صفحات هبوط تسويقية للأكاديمية", label_en: "Marketing landing page builder for academy" },
  [K.PAGE_BUILDER_PRODUCT]: { category: C.CORE, label_ar: "محرر صفحات هبوط تسويقية للمنتج", label_en: "Marketing landing page builder for products" },
  [K.PAGE_BUILDER_COURSE]: { category: C.CORE, label_ar: "محرر صفحات هبوط تسويقية للدورة", label_en: "Marketing landing page builder for courses" },
  [K.TEAM_MANAGEMENT]: { category: C.CORE, label_ar: "فريق عمل بصلاحيات", label_en: "Team members with roles" },
  [K.MESSAGING]: { category: C.CORE, label_ar: "رسائل مباشرة مع الطلاب", label_en: "Direct messaging with students" },
  [K.GROUP_MESSAGING]: { category: C.CORE, label_ar: "رسائل جماعية", label_en: "Group messaging" },
  [K.NOTIFICATION_HISTORY]: { category: C.CORE, label_ar: "سجل الإشعارات", label_en: "Notification history" },
  [K.SENT_INVITES]: { category: C.CORE, label_ar: "تتبّع الدعوات المرسلة", label_en: "Sent invites tracking" },
  [K.RECEIPTS]: { category: C.CORE, label_ar: "إيصالات المدفوعات", label_en: "Payment receipts" },
  [K.COUPONS]: { category: C.CORE, label_ar: "كوبونات خصم", label_en: "Discount coupons" },

  // ── commerce ──────────────────────────────────────────────────────────────
  [K.STRIPE_PAYMENTS]: { category: C.COMMERCE, label_ar: "دفع إلكتروني (Stripe)", label_en: "Online payments (Stripe)" },
  [K.BANK_TRANSFER]: { category: C.COMMERCE, label_ar: "تحويلات بنكية احترافية للأفراد", label_en: "Pro bank transfers for individuals" },
  [K.ONE_TIME_PRODUCTS]: { category: C.COMMERCE, label_ar: "منتجات لمرة واحدة", label_en: "One-time products" },
  [K.BUNDLES]: { category: C.COMMERCE, label_ar: "حزم منتجات", label_en: "Product bundles" },
  [K.DIGITAL_PRODUCTS]: { category: C.COMMERCE, label_ar: "منتجات رقمية", label_en: "Digital products" },
  [K.RECURRING_PRODUCTS]: { category: C.COMMERCE, label_ar: "منتجات الاشتراك المتكرّر", label_en: "Recurring subscription products" },

  // ── emails ────────────────────────────────────────────────────────────────
  [K.AUTOMATED_EMAILS]: { category: C.EMAILS, label_ar: "إيميلات تلقائية", label_en: "Automated emails" },
  [K.EMAIL_TEMPLATES]: { category: C.EMAILS, label_ar: "قوالب بريد مخصّصة", label_en: "Custom email templates" },
  [K.EMAIL_CAMPAIGNS]: { category: C.EMAILS, label_ar: "حملات بريدية", label_en: "Email campaigns" },
  [K.EMAIL_LOGS]: { category: C.EMAILS, label_ar: "سجلات إرسال البريد", label_en: "Email send logs" },

  // ── certificates (Pro) ────────────────────────────────────────────────────
  [K.CERTIFICATES_AUTO]: {
    category: C.GROWTH_PRO,
    label_ar: "شهادات إتمام تلقائية",
    label_en: "Auto completion certificates",
    description_ar: "أصدر شهادات إتمام تلقائية لطلابك عند إنهاء الدورة.",
    description_en: "Automatically issue completion certificates to students.",
    icon: "Award",
    student_requestable: true,
  },
  [K.CERTIFICATES_MANUAL]: {
    category: C.GROWTH_PRO,
    label_ar: "إصدار شهادات يدوي",
    label_en: "Manual certificate issuance",
    description_ar: "أصدر شهادات يدوياً لطلاب محدّدين متى شئت.",
    description_en: "Issue certificates manually to specific students.",
    icon: "Award",
    student_requestable: true,
  },
  [K.CERTIFICATES_PRO_EXTRAS]: {
    category: C.GROWTH_PRO,
    label_ar: "قوالب شهادات + توقيع وتخصيص متقدّم",
    label_en: "Certificate templates + signature & advanced customization",
    description_ar: "قوالب متعدّدة، شعار وتوقيع، اتجاه أفقي/عمودي، إصدار جماعي.",
    description_en: "Multiple templates, logo & signature, orientation, bulk issue.",
    icon: "Award",
  },

  // ── sessions (Pro) ────────────────────────────────────────────────────────
  [K.LIVE_SESSIONS]: {
    category: C.SESSIONS_PRO,
    label_ar: "جلسات حية (خاصة وجماعية)",
    label_en: "Live sessions (private & group)",
    description_ar: "أنشئ جلسات حية فردية أو جماعية مع طلابك عبر الفيديو.",
    description_en: "Run private or group live video sessions with your students.",
    marketing_ar: "حوّل دوراتك لتجربة تفاعلية حية.",
    marketing_en: "Turn your courses into a live, interactive experience.",
    icon: "Video",
    student_requestable: true,
  },
  [K.RECURRING_SESSIONS]: {
    category: C.SESSIONS_PRO,
    label_ar: "جلسات متكرّرة وحجز تلقائي",
    label_en: "Recurring sessions & auto-booking",
    icon: "Repeat",
    student_requestable: true,
  },
  [K.SESSION_PACKAGES]: {
    category: C.SESSIONS_PRO,
    label_ar: "رزم جلسات (Session Packages)",
    label_en: "Session packages",
    icon: "Package",
  },
  [K.SESSION_LANDING]: { category: C.SESSIONS_PRO, label_ar: "محرر صفحة الجلسة", label_en: "Session landing builder" },
  [K.SESSIONS_CATALOG]: { category: C.SESSIONS_PRO, label_ar: "كاتالوج الجلسات العام", label_en: "Public sessions catalog" },

  // ── quiz (Pro) ────────────────────────────────────────────────────────────
  [K.QUIZZES]: {
    category: C.QUIZ_PRO,
    label_ar: "الكويزات والاختبارات",
    label_en: "Quizzes & exams",
    description_ar: "اختبارات ضمن الدورة + اختبار نهائي مع علامات.",
    description_en: "In-course quizzes + final exam with grading.",
    icon: "ListChecks",
    student_requestable: true,
  },
  [K.AI_QUIZZES]: {
    category: C.QUIZ_PRO,
    label_ar: "اختبارات بالذكاء الاصطناعي",
    label_en: "AI-powered quizzes",
    description_ar: "ولّد كويزات تلقائياً من محتوى الدرس بنقرة.",
    description_en: "Generate quizzes automatically from your lesson content.",
    icon: "Sparkles",
    highlight: true,
  },
  [K.QUESTION_BANK]: {
    category: C.QUIZ_PRO,
    label_ar: "بنك أسئلة متقدّم (MC, T/F, مطابقة)",
    label_en: "Advanced question bank (MC, T/F, matching)",
    icon: "Library",
  },
  [K.QUIZ_BLOCKING]: { category: C.QUIZ_PRO, label_ar: "حجب التقدّم بالكويز", label_en: "Quiz blocking mode" },

  // ── growth (Pro) ──────────────────────────────────────────────────────────
  [K.LIMITED_FREE_ACCESS]: {
    category: C.GROWTH_PRO,
    label_ar: "وصول مجاني محدود — حوّل الزوار لعملاء",
    label_en: "Limited Free Access — convert visitors to customers",
    icon: "Gift",
  },
  [K.VIP_MEMBERSHIP]: {
    category: C.GROWTH_PRO,
    label_ar: "عضويات VIP للأكاديمية — وصول كامل لكل المحتوى",
    label_en: "Academy VIP memberships — full access to all content",
    description_ar: "بع اشتراكاً واحداً يفتح كل المحتوى — دخل متكرّر شهرياً.",
    description_en: "Sell one subscription unlocking everything — recurring monthly revenue.",
    icon: "Crown",
    student_requestable: true,
  },
  [K.GRANTS_AND_INVITES]: {
    category: C.GROWTH_PRO,
    label_ar: "منح المنتجات والجلسات + الدعوات",
    label_en: "Grant products & sessions + invites",
    icon: "Send",
    student_requestable: true,
  },
  [K.COURSE_DISCUSSIONS]: {
    category: C.GROWTH_PRO,
    label_ar: "نقاشات الدورة",
    label_en: "Course discussions",
    icon: "MessageSquare",
    student_requestable: true,
  },
  [K.BANNERS]: {
    category: C.GROWTH_PRO,
    label_ar: "بانرات وإعلانات داخل الأكاديمية",
    label_en: "In-academy banners & announcements",
    description_ar: "اعرض إعلانات وعروض ترويجية لزوار وطلاب أكاديميتك.",
    description_en: "Show announcements and promos to your academy visitors and students.",
    icon: "Megaphone",
    student_requestable: true,
  },
  [K.CUSTOM_DOMAIN]: {
    category: C.GROWTH_PRO,
    label_ar: "نطاق مخصّص (yourname.com)",
    label_en: "Custom domain (yourname.com)",
    description_ar: "استخدم نطاقك الخاص بدلاً من نطاق Dwrrah الفرعي.",
    description_en: "Use your own domain instead of a Dwrrah subdomain.",
    icon: "Globe",
    student_requestable: true,
  },
  [K.GOOGLE_CALENDAR_SYNC]: {
    category: C.GROWTH_PRO,
    label_ar: "مزامنة تقويم Google",
    label_en: "Google Calendar sync",
    icon: "Calendar",
    student_requestable: true,
  },
  [K.SEO_LANDING]: {
    category: C.GROWTH_PRO,
    label_ar: "تحسين الظهور (SEO/AEO) لصفحات الهبوط والمنتجات",
    label_en: "SEO/AEO for landing & product pages",
    description_ar: "حسّن ظهور صفحاتك في Google وزِد عدد زوارك من البحث.",
    description_en: "Improve your pages' visibility in Google and grow organic traffic.",
    marketing_ar: "أداة تسويق مجانية — يجدك العملاء قبل أن تبحث عنهم.",
    marketing_en: "Free marketing channel — customers find you organically.",
    icon: "Search",
  },
  [K.HIDE_DWRRAH_ATTRIBUTION]: {
    category: C.GROWTH_PRO,
    label_ar: "إخفاء عبارة «صُنع بـ 💜 بواسطة منصة دورة» من موقعك",
    label_en: "Hide \"Made with 💜 by Dwrrah\" from your site",
    description_ar: "علامتك التجارية فقط — بدون أي إشارة لمنصة دورة في الفوتر.",
    description_en: "Your brand only — no Dwrrah attribution in your footer.",
    icon: "EyeOff",
  },
  [K.CUSTOM_HTML_BLOCK]: {
    category: C.GROWTH_PRO,
    label_ar: "كتل HTML/Embed مخصّصة",
    label_en: "Custom HTML / Embed blocks",
    description_ar: "أدرج Calendly، نماذج، أو أي كود طرف ثالث في صفحاتك.",
    description_en: "Embed Calendly, forms, or any third-party widget in your pages.",
    icon: "Code",
  },
  [K.HTML_TEMPLATES]: {
    category: C.GROWTH_PRO,
    label_ar: "قوالب HTML جاهزة لصفحات الهبوط",
    label_en: "Ready-made HTML landing templates",
    icon: "LayoutTemplate",
  },
  [K.ADVANCED_ANALYTICS]: {
    category: C.GROWTH_PRO,
    label_ar: "تحليلات وتقارير متقدّمة",
    label_en: "Advanced analytics & reports",
    description_ar: "تقارير متقدّمة عن الإيرادات، الطلاب، والأداء.",
    description_en: "Advanced reports on revenue, students, and performance.",
    icon: "BarChart3",
  },
  [K.TRACKING_INTEGRATIONS]: {
    category: C.GROWTH_PRO,
    label_ar: "أدوات التتبع والتحليلات",
    label_en: "Tracking & analytics integrations",
    description_ar: "اربط Meta Pixel وGoogle Analytics وGTM وTikTok وSnap وMicrosoft Clarity وPostHog بصفحة هبوط أكاديميتك بدون كود.",
    description_en: "Connect Meta Pixel, Google Analytics, GTM, TikTok, Snap, Microsoft Clarity and PostHog to your academy landing — no code.",
    icon: "Activity",
  },
};

export function getFeatureMeta(key) {
  return FEATURE_METADATA[key] || null;
}

/**
 * List of feature keys that the student-side blocked screen may offer
 * "request from instructor" for.
 */
export const STUDENT_REQUESTABLE_KEYS = Object.entries(FEATURE_METADATA)
  .filter(([, m]) => m?.student_requestable === true)
  .map(([k]) => k);